import {useEffect, useState} from 'react'
import {Form, Modal} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {CREATE_USER_FROM_ONBOARDING_APPLICATION} from '../../../gql/mutations/agentOnboardingMutations'
import {IAgentOnboardingApplication} from '../../../types/agentOnboarding'

const M = defineMessages({
  title: {id: 'onboarding.user.title', defaultMessage: 'Create system user'},
  hint: {
    id: 'onboarding.user.hint',
    defaultMessage:
      'Creates a CRM account with the Agent role, linked to this application. The agent receives an invitation email to set their own password.',
  },
  agent: {id: 'onboarding.user.agent', defaultMessage: 'Agent'},
  email: {id: 'onboarding.user.email', defaultMessage: 'Email'},
  emailHelp: {
    id: 'onboarding.user.emailHelp',
    defaultMessage: 'This will be the login for the CRM.',
  },
  npn: {id: 'onboarding.user.npn', defaultMessage: 'NPN'},
  npnHelp: {id: 'onboarding.user.npnHelp', defaultMessage: 'Required to register the agent.'},
  cancel: {id: 'onboarding.user.cancel', defaultMessage: 'Cancel'},
  submit: {id: 'onboarding.user.submit', defaultMessage: 'Create user'},
  createdInvited: {
    id: 'onboarding.user.createdInvited',
    defaultMessage: 'User created — invitation email sent',
  },
  createdNoEmail: {
    id: 'onboarding.user.createdNoEmail',
    defaultMessage: 'User created, but the invitation email could not be sent',
  },
  failed: {id: 'onboarding.user.failed', defaultMessage: 'Could not create the user'},
  invalidEmail: {id: 'onboarding.info.invalidEmail', defaultMessage: 'Enter a valid email address'},
})

const required = '*'

interface Props {
  application: IAgentOnboardingApplication | null
  onClose: () => void
  onCreated: () => void
}

export function ModalCreateAgentUser({application, onClose, onCreated}: Props) {
  const intl = useIntl()
  const [email, setEmail] = useState('')
  const [npn, setNpn] = useState('')
  const [createUser, {loading}] = useMutation(CREATE_USER_FROM_ONBOARDING_APPLICATION)

  // El correo y el NPN de la solicitud son el punto de partida, pero quedan editables:
  // el NPN suele emitirse después de que el agente llenó el formulario.
  useEffect(() => {
    setEmail(application?.email ?? '')
    setNpn(application?.npn ?? '')
  }, [application])

  const emailError =
    email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())
      ? intl.formatMessage(M.invalidEmail)
      : undefined
  const canSubmit = !!email.trim() && !!npn.trim() && !emailError

  const handleSubmit = async () => {
    if (!application) return
    try {
      const res = await createUser({
        variables: {
          applicationId: application.id,
          npn: npn.trim(),
          email: email.trim(),
          password: null,
        },
      })
      const payload = res.data?.createUserFromOnboardingApplication
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      // El aviso distingue si el correo salió: sin él la cuenta existe pero el agente no puede
      // entrar, y eso hay que saberlo en el momento, no cuando el agente reclame.
      toast.success(
        intl.formatMessage(payload.invitationSent ? M.createdInvited : M.createdNoEmail)
      )
      onCreated()
      onClose()
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <Modal show={!!application} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='alert alert-light-primary fs-7'>{intl.formatMessage(M.hint)}</div>

        <div className='mb-4'>
          <label className='form-label fw-semibold'>{intl.formatMessage(M.agent)}</label>
          <div className='text-gray-900 fw-bold'>{application?.fullName}</div>
        </div>

        <div className='mb-4'>
          <label className='form-label fw-semibold'>
            {intl.formatMessage(M.email)} <span className='text-danger'>{required}</span>
          </label>
          <Form.Control
            type='email'
            value={email}
            isInvalid={!!emailError}
            onChange={(e) => setEmail(e.target.value)}
          />
          {emailError ? (
            <small className='text-danger'>{emailError}</small>
          ) : (
            <small className='text-gray-500'>{intl.formatMessage(M.emailHelp)}</small>
          )}
        </div>

        <div>
          <label className='form-label fw-semibold'>
            {intl.formatMessage(M.npn)} <span className='text-danger'>{required}</span>
          </label>
          <Form.Control value={npn} onChange={(e) => setNpn(e.target.value)} />
          <small className='text-gray-500'>{intl.formatMessage(M.npnHelp)}</small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose}>
          {intl.formatMessage(M.cancel)}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
        >
          <i className='bi bi-person-plus me-1' />
          {intl.formatMessage(M.submit)}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
